import React, { ChangeEvent, useState } from 'react';
import { GET_QUOTE_DOCUMENTS_getQuotesInformation_documentsList } from './graphql/__generated__/GET_QUOTE_DOCUMENTS';
import usePagination from '../../util/usePagination';
import {
  DataGroup,
  DataText,
  OuterBox
} from '../../components/quotesInfo/QuotesInfoStyles';
import styled from 'styled-components';
import { CardLayout } from '../../components/cardLayout/CardLayout';
import { Box, ResponsiveContext } from 'grommet';
import { AltInfo, BlackCross, Download } from '../../icons';
import pdfIcon from '../../assets/images/PDF Icon.svg';
import { title } from 'process';
import { ElementWrap } from '../../components/gpaCancelPolicy/RefundMethodsStyles';
import { BrowseButton, DocumentUpload, DocumentUploadZWrap, ElementWrapQuotes, FileName } from '../../components/cancelPolicy/cancelPolicyForm/cancelPolicyFormStyles';
import { UploadButton } from '../../components/documentsTable/DocumentsTable';
import UploadModal from '../../components/uploadModal';
import DocumentUploadModal from './DocumentUploadModal';
import { Trash } from 'grommet-icons';
import { formatDate } from '../../util/formatters';
import { ErrorMessage } from '../../components/changePolicy/changePolicyStyles';

interface DocumentsTableProps {
  quoteDocuments:
  | (GET_QUOTE_DOCUMENTS_getQuotesInformation_documentsList | null)[]
  | null;
  downloadDocument: Function;
  title: string;
  isUploadAvailable: boolean;
  uploadToken: string;
  uploadDoc: Function;
  sourceSystemCode: string;
  quoteNo: string | null;
  deleteQuoteDocument: Function;
  onDelete: Function;
  isDeleteAllowed: boolean;
}

export const Data = styled.li`
  display: flex;
  justify-content: center;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 10px;
  background-color: #f5f7fa;
  word-break: break-word;
  &:nth-child(1) {
    width: 5%;
  }
  &:nth-child(2) {
    width: 30%;
  }
  &:nth-child(3) {
    width: 30%;
  }
  &:nth-child(4) {
    width: 25%;
  }
  &:nth-child(5) {
    width: 10%;
  }
  &:nth-child(6) {
    width: 10%;
  }
`;

export const TextData = styled.li`
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    height: 100%;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  color: #041e41;
  font-family: 'PT Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  list-style: none;
  margin: 0;
  padding: 10px;
  word-break: break-all;
  &:nth-child(1) {
    width: 5%;
  }
  &:nth-child(2) {
    width: 30%;
  }
  &:nth-child(3) {
    width: 30%;
  }
  &:nth-child(4) {
    width: 25%;
  }
  &:nth-child(5) {
    width: 10%;
  }
  &:nth-child(6) {
    width: 10%;
  }
`;

export default ({
  quoteDocuments,
  downloadDocument,
  title,
  isUploadAvailable,
  uploadToken,
  uploadDoc,
  sourceSystemCode,
  quoteNo,
  deleteQuoteDocument,
  onDelete,
  isDeleteAllowed
}: DocumentsTableProps) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [staging, setStaging] = useState<boolean>(false);
  const [permissionMessage, setPermissionMessage] = useState<string | null>(null);
  const handleNoPermission = (docName: string) => {
    setPermissionMessage(`You do not have permission to delete "${docName}"`);
  };
  const [paginatedQuoteDocs, pageSelector] = usePagination({
    items: quoteDocuments && quoteDocuments?.length > 0 ? quoteDocuments : []
  });
  let columns = [
    {
      property: 'icon',
      header: <DataText></DataText>
    },
    {
      property: 'name',
      header: <DataText>NAME</DataText>
    },
    {
      property: 'type',
      header: <DataText>DOCUMENT TYPE</DataText>
    },
    {
      property: 'date',
      header: <DataText>DATE UPLOADED</DataText>
    },
    {
      property: 'download',
      header: <DataText>Download</DataText>
    }
  ];

  if (!!isUploadAvailable && isDeleteAllowed) {
    columns = [...columns, {
      property: 'Remove',
      header: <DataText>Remove</DataText>
    }];
  }

  const IconContainer = styled.div`
    width: 22px;
  `;

  return (
    <CardLayout title={title}>
      <ResponsiveContext.Consumer>
        {responsive => (
          <Box background="white">
            {!!permissionMessage && (<ErrorMessage test-attr="policy-change-error-message" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: 'fit-content' }}>
              {permissionMessage}
              <IconContainer onClick={() => setPermissionMessage(null)}>
                <BlackCross />
              </IconContainer>
            </ErrorMessage>)}
            {(openModal && isUploadAvailable) && (
              <div style={{ position: 'fixed' }}>
                <DocumentUploadModal
                  staging={staging}
                  showModalHandler={setOpenModal}
                  stagingHandler={setStaging}
                  readOnlyAccess={false}
                  readOnlyFeatureFlag={false}
                  uploadToken={uploadToken}
                  uploadDoc={uploadDoc}
                  sourceSystemCode={sourceSystemCode}
                  quoteNo={quoteNo}
                />
              </div>)}
            < Box
              fill={
                responsive === 'small' || responsive === 'medium'
                  ? false
                  : 'horizontal'
              }
              responsive
              overflow="auto"
            >
              {paginatedQuoteDocs && paginatedQuoteDocs?.length > 0 ? (
                <Box>
                  <Box direction="row">
                    {columns.map(column => (
                      <Data key={column.property}>{column.header}</Data>
                    ))}
                  </Box>

                  <Box>
                    {paginatedQuoteDocs &&
                      paginatedQuoteDocs.map((doc, index) => {
                        return (
                          <Box
                            border={
                              index === paginatedQuoteDocs.length
                                ? { side: 'bottom', color: 'white' }
                                : { side: 'bottom', color: '#D9DFEB' }
                            }
                            key={doc?.sessionID}
                          >
                            <OuterBox>
                              <DataGroup>
                                <TextData>
                                  <IconContainer>
                                    <img src={pdfIcon} alt="PDF" />
                                  </IconContainer>
                                </TextData>
                                <TextData>{doc?.name}</TextData>
                                <TextData>{doc?.documentType}</TextData>
                                <TextData>{formatDate(doc?.dateModified_SA)}</TextData>
                                <TextData
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    downloadDocument({
                                      variables: {
                                        getDocById: true,
                                        documentID: doc?.docUID
                                      }
                                    });
                                  }}
                                >
                                  <Download />
                                </TextData>
                                {isUploadAvailable && isDeleteAllowed && (<TextData style={{ cursor: 'pointer', height: '20px', marginTop: '10px' }}
                                  onClick={() => {
                                    if (!doc?.canDelete) {
                                      handleNoPermission(doc?.name);
                                      return;
                                    }
                                    onDelete(doc?.publicID, sourceSystemCode);
                                  }}
                                ><Trash /></TextData>)}
                              </DataGroup>
                            </OuterBox>
                          </Box>
                        );
                      })}
                  </Box>
                  <Box align="end">{pageSelector}</Box>
                </Box>
              ) : (
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    padding: '10px',
                    alignItems: 'center'
                  }}
                >
                  <Box style={{ marginRight: '10px' }}>
                    <AltInfo />
                  </Box>
                  <Box>There are no documents associated with this quote.</Box>
                </Box>
              )}

              {isUploadAvailable && (<ElementWrapQuotes error={false}>
                <UploadButton
                  onClick={() => {
                    setOpenModal(true);
                  }}
                >UPLOAD DOCUMENT</UploadButton>
              </ElementWrapQuotes>)}
            </Box>
          </Box>
        )}
      </ResponsiveContext.Consumer>
    </CardLayout >
  );
};
